"use client";
import { NavigationMenu } from "radix-ui";
import { StyledLink } from "~/components/Link";
import { TaxonomyCard } from "~/components/cards/TaxonomyCard";
import type { getWebsite } from "~/graphql/shared/website";
import { useRouter } from "~/hooks/useRouter";

type RootCategoryType = Awaited<ReturnType<typeof getWebsite>>["current"]["rootCategory"];

interface CategoryOptions {
	id?: string;
	name: string;
	label?: string;
	buttons: {
		name: string;
		href?: string;
	}[];
}

interface NavigationProps {
	lang: string;
	category: RootCategoryType;
	mainCategory: CategoryOptions;
	secondaryCategory: CategoryOptions;
}

export function Navigation({ lang, mainCategory, secondaryCategory, category }: NavigationProps) {
	const first = category?.children.find((child) => child.id === mainCategory.id);
	const second = category?.children.find((child) => child.id === secondaryCategory.id);

	return (
		<NavigationMenu.Root className="flex">
			<NavigationMenu.List className="flex items-center space-x-4 xl:space-x-6">
				{first ? <NavigationItem lang={lang} category={first} options={mainCategory} /> : null}
				{second ? (
					<NavigationItem lang={lang} category={second} options={secondaryCategory} amount={6} />
				) : null}
			</NavigationMenu.List>

			<div className="perspective-[2000px] fixed top-[var(--header-y,80px)] left-0 flex w-screen justify-center">
				<NavigationMenu.Viewport className="relative h-[var(--radix-navigation-menu-viewport-height)] w-full origin-[top_center] overflow-hidden bg-shades-light transition-[width,_height] duration-150 data-[state=closed]:animate-scaleOut data-[state=open]:animate-scaleIn sm:w-[var(--radix-navigation-menu-viewport-width)]" />
			</div>
		</NavigationMenu.Root>
	);
}

interface NavigationItemProps {
	lang: string;
	category: RootCategoryType["children"][number];
	options: CategoryOptions;
	extraCategories?: RootCategoryType["children"];
	amount?: number;
}

function NavigationItem({ lang, category, options, amount = 7 }: NavigationItemProps) {
	const router = useRouter();

	return (
		<NavigationMenu.Item value={category.id}>
			<NavigationMenu.Trigger
				className="group inline-flex appearance-none items-start"
				onClick={(e) => {
					e.preventDefault();

					return e.metaKey ? router.tab(category.url) : router.push(category.url);
				}}
			>
				<span className="whitespace-nowrap rounded font-extrabold font-primary text-lg text-primary-medium group-hover:underline">
					{options.name}
				</span>

				{options.label !== undefined && (
					<span className="ml-1 whitespace-nowrap rounded-xl bg-secondary px-1.5 py-0.5 font-bold font-sans text-white text-xs leading-none no-underline">
						{options.label}
					</span>
				)}
			</NavigationMenu.Trigger>

			<NavigationMenu.Content className="w-screen data-[motion=from-end]:animate-enterFromRight data-[motion=from-start]:animate-enterFromLeft data-[motion=to-end]:animate-exitToRight data-[motion=to-start]:animate-exitToLeft">
				<div className="flex flex-col space-y-4 p-4">
					<ul className="flex flex-wrap justify-center">
						{category.children.slice(0, amount).map((child) => (
							<li key={child.id}>
								<TaxonomyCard lang={lang} item={child} variant="nav" className="m-2" />
							</li>
						))}
					</ul>

					<div className="flex items-center justify-center gap-4">
						{options.buttons
							.filter((button) => !!button.href)
							.map((button, index) => (
								<StyledLink
									key={index}
									href={button.href}
									variant={index % 2 === 0 ? "contained" : "outlined"}
									color={index % 2 === 0 ? "success" : undefined}
								>
									{button.name}
								</StyledLink>
							))}
					</div>
				</div>
			</NavigationMenu.Content>
		</NavigationMenu.Item>
	);
}
